import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import useLanguage from "../utils/useLanguage";

const Tooltip = ({
  children,
  content,
  onShow,
  maxWidth,
  direction,
  textAlign,
  position = "top",
  bgColor = "neutral",
}) => {
  const { t } = useTranslation("general");
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [actualPosition, setActualPosition] = useState(position);

  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  const {  currentDirection } = useLanguage();

  const tooltipId = `tooltip-${Math.random().toString(36).substring(2, 9)}`;
  const colorClass = bgColor ? `tooltip-bg-${bgColor}` : "tooltip-bg-default";

  const calculatePosition = () => {
    if (!triggerRef.current) return { top: 0, left: 0, position };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;

    // Measure tooltip size dynamically
    const tooltipWidth = tooltipRef.current?.offsetWidth || maxWidth || 150;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 60;

    const offset = 10;
    const arrowSize = 6;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const padding = 10;

    // Calculate absolute position of trigger
    const triggerTop = triggerRect.top + scrollY;
    const triggerLeft = triggerRect.left + scrollX;

    // Determine best position based on available space
    let finalPosition = position;
    let top = 0;
    let left = 0;

    // Check space availability for smart positioning
    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = viewportWidth - triggerRect.right;

    // Smart positioning logic (prioritize based on default position)
    if (
      position === "top" &&
      spaceAbove < tooltipHeight + offset &&
      spaceBelow > tooltipHeight + offset
    ) {
      finalPosition = "bottom";
    } else if (
      position === "bottom" &&
      spaceBelow < tooltipHeight + offset &&
      spaceAbove > tooltipHeight + offset
    ) {
      finalPosition = "top";
    } else if (
      position === "left" &&
      spaceLeft < tooltipWidth + offset &&
      spaceRight > tooltipWidth + offset
    ) {
      finalPosition = "right";
    } else if (
      position === "right" &&
      spaceRight < tooltipWidth + offset &&
      spaceLeft > tooltipWidth + offset
    ) {
      finalPosition = "left";
    }

    // Calculate position based on final position
    switch (finalPosition) {
      case "top":
        top = triggerTop - tooltipHeight - offset - arrowSize;
        left = triggerLeft + triggerRect.width / 2 - tooltipWidth / 2;
        break;
      case "bottom":
        top = triggerTop + triggerRect.height + offset + arrowSize;
        left = triggerLeft + triggerRect.width / 2 - tooltipWidth / 2;
        break;
      case "right":
        top = triggerTop + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerLeft + triggerRect.width + offset + arrowSize;
        break;
      case "left":
        top = triggerTop + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerLeft - tooltipWidth - offset - arrowSize;
        break;
      default:
        top = triggerTop - tooltipHeight - offset - arrowSize;
        left = triggerLeft + triggerRect.width / 2 - tooltipWidth / 2;
    }

    // Keep tooltip within horizontal viewport bounds
    if (left < scrollX + padding) {
      left = scrollX + padding;
    } else if (left + tooltipWidth > scrollX + viewportWidth - padding) {
      left = scrollX + viewportWidth - tooltipWidth - padding;
    }

    // Keep tooltip within vertical viewport bounds
    if (top < scrollY + padding) {
      top = scrollY + padding;
    } else if (top + tooltipHeight > scrollY + viewportHeight - padding) {
      top = scrollY + viewportHeight - tooltipHeight - padding;
    }

    return { top, left, position: finalPosition };
  };

  const updatePosition = () => {
    const result = calculatePosition();
    setCoords({ top: result.top, left: result.left });
    setActualPosition(result.position);
  };

  useEffect(() => {
    if (!isVisible) return;

    // First calculation (before measurements)
    updatePosition();

    // Second calculation after browser paints tooltip and measurements are available
    requestAnimationFrame(() => {
      updatePosition();
    });
  }, [isVisible, content]);

  const showTooltip = async () => {
    clearTimeout(timeoutRef.current);
    setIsVisible(true);
    if (onShow) {
      onShow();
    }
  };

  const hideTooltip = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  const handleMouseEnter = () => showTooltip();
  const handleMouseLeave = () => hideTooltip();
  const handleFocus = () => showTooltip();
  const handleBlur = () => hideTooltip();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const tooltipElement = isVisible ? (
    <div
      ref={tooltipRef}
      className={`c-tooltip ${colorClass} c-tooltip--${actualPosition}`}
      id={tooltipId}
      role="tooltip"
      style={{
        textAlign: textAlign ? textAlign : "center",
        direction: direction ? direction : currentDirection,
        maxWidth: maxWidth ? maxWidth : "150px",
        position: "absolute",
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        zIndex: 10000,
        pointerEvents: "none",
      }}
    >
      {content}
    </div>
  ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        className="tooltip-trigger"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-describedby={isVisible ? tooltipId : undefined}
        tabIndex={0}
      >
        {children}
      </span>
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  );
};

export default Tooltip;
